import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Info, TrendingDown, ShieldCheck, Clock, ChevronDown, Sparkles, Coins, Percent, Ban, ShieldAlert, HelpCircle } from 'lucide-react';

/**
 * PointsExplainer — Transparency card + T&C for the referral / rewards program.
 * Renders on the Invite & Earn page. All values are pulled from live settings.
 */
export const PointsExplainer = ({ pointsPerInr = 5, cashbackPct = 4, globalCapFree = 300, globalCapPaid = 600, inactivityMonths = 12, examples = [] }) => {
  const [open, setOpen] = useState(true);
  const [tcOpen, setTcOpen] = useState(false);

  return (
    <div className='space-y-3' data-testid='points-explainer-card'>
      <Collapsible open={open} onOpenChange={setOpen}>
        <Card className='p-0 overflow-hidden border-primary/20'>
          <CollapsibleTrigger className='w-full flex items-center justify-between p-4 bg-primary/5 hover:bg-primary/10 transition-colors' data-testid='toggle-how-points-work'>
            <div className='flex items-center gap-2 text-left'>
              <Info className='h-5 w-5 text-primary shrink-0' />
              <div>
                <div className='font-semibold text-sm sm:text-base'>How the IndusFact points system works</div>
                <div className='text-xs text-muted-foreground'>Earn, redeem, save — fully transparent.</div>
              </div>
            </div>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className='p-5 space-y-4'>
              {/* Three quick facts */}
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
                <div className='p-3 rounded-lg border bg-card space-y-1'>
                  <div className='flex items-center gap-1.5 text-xs uppercase text-muted-foreground'><Coins className='h-3.5 w-3.5' /> Conversion</div>
                  <div className='text-lg font-semibold'><span className='text-primary'>{pointsPerInr}</span> points = <span className='text-primary'>₹1</span> value</div>
                  <div className='text-[11px] text-muted-foreground'>Fixed & transparent — same across every reward.</div>
                </div>
                <div className='p-3 rounded-lg border bg-card space-y-1'>
                  <div className='flex items-center gap-1.5 text-xs uppercase text-muted-foreground'><TrendingDown className='h-3.5 w-3.5' /> Loyalty discount</div>
                  <div className='text-lg font-semibold'><span className='text-primary'>~10% off</span> retail</div>
                  <div className='text-[11px] text-muted-foreground'>Redeeming with points always costs ~10% less than paying cash.</div>
                </div>
                <div className='p-3 rounded-lg border bg-card space-y-1'>
                  <div className='flex items-center gap-1.5 text-xs uppercase text-muted-foreground'><Sparkles className='h-3.5 w-3.5' /> Extra reward</div>
                  <div className='text-lg font-semibold'><span className='text-primary'>{cashbackPct}%</span> cashback</div>
                  <div className='text-[11px] text-muted-foreground'>Get {cashbackPct}% of every purchase back as points (uncapped).</div>
                </div>
              </div>

              {/* Cash vs Points comparison — LIVE prices from the rewards API
                  (never hardcoded; follows the admin-configured base price). */}
              {examples.length > 0 && (
              <div className='rounded-lg border overflow-hidden'>
                <div className='px-4 py-2 bg-secondary text-xs uppercase font-semibold text-muted-foreground flex items-center gap-1.5'>
                  <Percent className='h-3.5 w-3.5' /> Paying with points vs cash — real examples
                </div>
                <div className='divide-y'>
                  {examples.map((row, i) => {
                    const ptsValue = Math.round(row.pts / pointsPerInr);
                    const saved = Math.max(0, Math.round(row.retail - ptsValue));
                    const pct = row.retail > 0 ? Math.round((saved / row.retail) * 100) : 0;
                    return (
                      <div key={i} className='grid grid-cols-4 items-center px-4 py-2 text-sm gap-2' data-testid={`pts-compare-${i}`}>
                        <div className='font-medium col-span-2 sm:col-span-1 truncate'>{row.name}</div>
                        <div className='text-muted-foreground text-xs'>Cash: <strong className='text-foreground'>₹{row.retail.toLocaleString('en-IN')}</strong></div>
                        <div className='text-muted-foreground text-xs'>Points: <strong className='text-primary'>{row.pts.toLocaleString('en-IN')} pts</strong> <span className='opacity-70'>(≈ ₹{ptsValue})</span></div>
                        <div className='text-right'><Badge variant='outline' className='text-[10px] border-[hsl(var(--verified))]/40 text-[hsl(var(--verified))]'>Save ₹{saved} · {pct}%</Badge></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Global cap explainer */}
              <div className='rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground flex items-start gap-2'>
                <ShieldCheck className='h-4 w-4 text-primary shrink-0 mt-0.5' />
                <div>
                  Free users can earn up to <strong className='text-foreground'>{globalCapFree} pts / month</strong> from platform activities (≈ ₹{Math.round(globalCapFree / pointsPerInr)}). Paying subscribers can earn up to <strong className='text-foreground'>{globalCapPaid} pts / month</strong>. <strong className='text-foreground'>Referrals and purchase cashback are UNCAPPED</strong> — they always keep earning.
                </div>
              </div>

              {/* Terms & Conditions collapsible */}
              <Collapsible open={tcOpen} onOpenChange={setTcOpen}>
                <CollapsibleTrigger className='w-full flex items-center justify-between p-3 rounded-lg border hover:bg-secondary text-sm' data-testid='toggle-points-tc'>
                  <span className='flex items-center gap-2'><HelpCircle className='h-4 w-4 text-primary' /> <strong>Terms & Conditions</strong> — the fine print (must-read)</span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${tcOpen ? 'rotate-180' : ''}`} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className='mt-2 rounded-lg border p-4 space-y-3 text-xs' data-testid='points-tc-content'>
                    <TcItem icon={Coins} title='Point value'>
                      Every point is worth exactly <strong>₹{(1 / pointsPerInr).toFixed(2)}</strong> ({pointsPerInr} pts = ₹1). The ratio is locked and applies uniformly to every redeemable reward.
                    </TcItem>
                    <TcItem icon={Percent} title='Loyalty discount built-in'>
                      Every reward is priced ~10% below its cash equivalent. So paying with points always saves you money vs paying in cash for the same item.
                    </TcItem>
                    <TcItem icon={ShieldCheck} title='Monthly earning caps'>
                      To keep the program sustainable, non-referral / non-cashback activities are capped at {globalCapFree} pts / month for free users and {globalCapPaid} pts / month for paying subscribers. Referrals and purchase cashback have <strong>no cap</strong>.
                    </TcItem>
                    <TcItem icon={Clock} title='Point expiry'>
                      Points expire only after <strong>{inactivityMonths} consecutive months of no login</strong>. Active users never lose points — log in at least once a year to keep everything.
                    </TcItem>
                    <TcItem icon={Ban} title='Anti-abuse'>
                      Self-referrals are not allowed. Multiple accounts using the same GST beyond {3} accounts, or multiple signups from the same IP within 30 days, are auto-flagged for admin review. Confirmed abuse forfeits all earned points.
                    </TcItem>
                    <TcItem icon={ShieldAlert} title='Refunds & rollbacks'>
                      If a purchase is refunded, the corresponding cashback points are also debited from your balance. Referral rewards are rolled back if the referred user is later blocked for fraud.
                    </TcItem>
                    <TcItem icon={Info} title='Program changes'>
                      Point values, caps and rewards can be updated by IndusFact for future users. Points already earned remain valid at the ratio active when they were credited. We will announce material changes 30 days in advance via email + in-app banner.
                    </TcItem>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

const TcItem = ({ icon: Icon, title, children }) => (
  <div className='flex items-start gap-2'>
    <Icon className='h-3.5 w-3.5 text-primary shrink-0 mt-0.5' />
    <div>
      <div className='font-semibold text-foreground'>{title}</div>
      <div className='text-muted-foreground'>{children}</div>
    </div>
  </div>
);

export default PointsExplainer;
